/**
 * fab-size-key.js
 *
 * Orientation-independent size keys for the collapsed-tabbed size picker.
 * Used to match Shopify option values against the configurable Popular list.
 *
 * Standard sizes → smaller×larger inch pair, e.g. "16x20".
 * A-sizes → "A5" … "A0" (uppercase).
 */
(function (root, factory) {
  'use strict';

  var FabSizeKey = factory();

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = FabSizeKey;
  }
  root.FabSizeKey = FabSizeKey;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  function normX(str) {
    return String(str || '')
      .replace(/\u00d7/g, 'x')
      .replace(/×/g, 'x')
      .replace(/″/g, '"')
      .replace(/"/g, '')
      .trim();
  }

  /**
   * Parse "WxH" from a dimension fragment (cm or in).
   * @returns {{ w: number, h: number } | null}
   */
  function parseDimPair(fragment) {
    if (!fragment) return null;
    var m = normX(fragment).match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/i);
    if (!m) return null;
    var w = parseFloat(m[1]);
    var h = parseFloat(m[2]);
    if (!w || !h) return null;
    return { w: w, h: h };
  }

  /**
   * Normalise WxH to smaller×larger integer pair string, e.g. "16x20".
   */
  function normalizeDimKey(w, h) {
    var a = Math.round(w);
    var b = Math.round(h);
    if (a <= b) return a + 'x' + b;
    return b + 'x' + a;
  }

  /**
   * Derive a stable popular-matching key from a Shopify Size option value.
   *
   * @param {string} raw — e.g. "40x50cm / 16x20in", "50x40cm / 20x16in", "A4 / A4"
   * @returns {string} — e.g. "16x20", "A4", or "" if unknown
   */
  function normalizePopularKey(raw) {
    if (!raw) return '';

    var parts = String(raw).split('/').map(function (p) {
      return p.trim();
    });
    var cmPart = parts[0] || '';
    var inPart = parts[1] || '';

    var upper = cmPart.toUpperCase();
    var aMatch = upper.match(/^A([0-7])\b/);
    if (aMatch) return 'A' + aMatch[1];

  // Also check inPart / full string for A-sizes like "A4 / A4"
    if (!aMatch && inPart) {
      var inUpper = inPart.toUpperCase();
      var aMatch2 = inUpper.match(/^A([0-7])\b/);
      if (aMatch2) return 'A' + aMatch2[1];
    }

    var inPair = parseDimPair(inPart);
    if (inPair) return normalizeDimKey(inPair.w, inPair.h);

    var cmPair = parseDimPair(cmPart);
    if (cmPair) {
      var cmShort = Math.round(Math.min(cmPair.w, cmPair.h));
      var cmLong = Math.round(Math.max(cmPair.w, cmPair.h));
      if (cmShort === 30 && cmLong === 60) return '12x24';
      if (cmShort === 50 && cmLong === 100) return '20x40';
      if (cmShort === 75 && cmLong === 150) return '30x60';

      // Convert cm to inches for canonical key (matches merchandising ladder).
      var wIn = Math.round((cmPair.w / 2.54) * 10) / 10;
      var hIn = Math.round((cmPair.h / 2.54) * 10) / 10;
      return normalizeDimKey(wIn, hIn);
    }

    return '';
  }

  /**
   * True when a product option value matches a configured popular key.
   */
  function valueMatchesPopularKey(raw, popularKey) {
    if (!raw || !popularKey) return false;
    return normalizePopularKey(raw).toUpperCase() === String(popularKey).trim().toUpperCase();
  }

  /**
   * True for 36×48 in (and metric equivalents such as 90×120 cm).
   */
  function isXxlSizeKey(raw) {
    if (!raw) return false;
    if (normalizePopularKey(raw) === '36x48') return true;

    var s = String(raw)
      .toLowerCase()
      .replace(/\u00d7/g, 'x')
      .replace(/×/g, 'x')
      .trim();

    var cmMatch = s.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*cm/);
    if (cmMatch) {
      var cw = Math.round(parseFloat(cmMatch[1], 10));
      var ch = Math.round(parseFloat(cmMatch[2], 10));
      if ((cw === 90 && ch === 120) || (cw === 120 && ch === 90)) return true;
    }

    if (/90\s*x\s*120|120\s*x\s*90/.test(s)) return true;
    if (/36\s*x\s*48|48\s*x\s*36/.test(s)) return true;

    return false;
  }

  return {
    normalizePopularKey: normalizePopularKey,
    valueMatchesPopularKey: valueMatchesPopularKey,
    isXxlSizeKey: isXxlSizeKey,
    parseDimPair: parseDimPair,
    normalizeDimKey: normalizeDimKey,
  };
});
